import { useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import * as Tabs from "@radix-ui/react-tabs";
import {
  Activity,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  ChevronRight,
  Clock3,
  ExternalLink,
  FileText,
  Fingerprint,
  Globe,
  History,
  Layers3,
  LayoutGrid,
  MessageSquare,
  Package,
  Search,
  Sparkles,
  WandSparkles,
  X,
} from "lucide-react";
import { GlossTile } from "@/components/panels";
import {
  hermesSkillDetails,
  usesHermesDescription,
  hermesReferenceText,
} from "@/components/agent/hermesSkillDetails";
import { useChat } from "@/components/chat/ChatWorkspaceContext";
import {
  customerSkillDescription,
  customerSkillText,
  isClientVisibleSkill,
} from "@/components/agent/skillPresentation";
import { ChatDraftLaunch } from "@/components/automations/AutomationFocusList";
import {
  formatActionLabel,
  formatExactDate,
  formatLastUsed,
  formatPromotionLabel,
  formatDriftLabel,
  parseTimestamp,
} from "@/components/agent/skillUpdates";
import {
  SKILL_GROUP_LABELS,
  isRemoved,
  skillKey,
  skillGroup,
  skillOrigin,
  skillTitle,
  versionLabel,
  stateLabel,
  visibleSkills,
  skillChanges,
} from "./skillLibraryModel";
import "./skills.css";

const GROUPS = [
  {
    key: "hermes",
    title: SKILL_GROUP_LABELS.hermes,
    eyebrow: "YOUR AGENT’S FOUNDATION",
    icon: Package,
    color: "#99550c",
    wash: "#fff6e7",
    border: "#efd4a7",
    hint: "The core agent toolkit",
    description: "Core capabilities supplied by the default Hermes toolkit.",
  },
  {
    key: "growthos",
    title: SKILL_GROUP_LABELS.growthos,
    eyebrow: "YOUR SHARED TOOLKIT",
    icon: Layers3,
    color: "#245dc1",
    wash: "#edf4ff",
    border: "#c4d7f6",
    hint: "Shared growth workflows",
    description:
      "Shared GrowthOS skills that bring our workflows to your agent.",
  },
  {
    key: "brand",
    title: SKILL_GROUP_LABELS.brand,
    eyebrow: "MADE FOR YOUR WORKSPACE",
    icon: Fingerprint,
    color: "#087b65",
    wash: "#eaf8f2",
    border: "#b8e0d0",
    hint: "Tailored to your brand",
    description:
      "Skills created for your brand, or adapted to the way you work.",
  },
];
const FILTER_GROUPS = [
  {
    key: "all",
    title: "All skills",
    icon: LayoutGrid,
    color: "#344967",
    wash: "#f0f3f8",
    border: "#cfd9e6",
    hint: "Explore your full toolkit",
  },
  ...GROUPS,
];
const groupStyle = ({ color, wash, border }) => ({
  "--sk-color": color,
  "--sk-wash": wash,
  "--sk-border": border,
});

// Icons describe the work; colors consistently identify the published origin.
function skillVisual(skill) {
  const name = String(skill.name || "").toLowerCase();
  const origin = GROUPS.find((item) => item.key === skillGroup(skill));
  const icon = /report|analytic|metric|research|audit/.test(name)
    ? Activity
    : /content|blog|copy|write|email|document/.test(name)
      ? FileText
      : /browser|web|search/.test(name)
        ? Globe
        : origin.key === "brand"
          ? Fingerprint
          : WandSparkles;
  return { ...origin, icon };
}
function SkillState({ skill }) {
  const active = String(skill.status).toLowerCase() === "active";
  return (
    <span className={`sk-state ${active ? "is-active" : ""}`}>
      <i />
      {stateLabel(skill.status)}
    </span>
  );
}
function SkillCard({ skill, onOpen }) {
  const visual = skillVisual(skill);
  const reference = hermesSkillDetails(skill);
  const lastUsed = formatLastUsed(skill.lastUsedAt);
  return (
    <button
      type="button"
      className="sk-card"
      style={groupStyle(visual)}
      onClick={onOpen}
      aria-label={`Explore ${skillTitle(skill)}`}
      data-testid={`skill-library-card-${skillKey(skill)}`}
    >
      <div className="sk-card-top">
        <GlossTile
          color={visual.color}
          icon={visual.icon}
          size={48}
          iconSize={23}
        />
        <SkillState skill={skill} />
      </div>
      <div className="sk-card-copy">
        <div className="sk-card-labels">
          <span className="sk-origin">{skillOrigin(skill)}</span>
          {reference?.category && (
            <span className="sk-category">{reference.category}</span>
          )}
        </div>
        <h3>{skillTitle(skill)}</h3>
        <p>
          {customerSkillDescription(skill) ||
            "Open this skill to see its origin, version, and recorded changes. A description hasn’t been shared yet."}
        </p>
      </div>
      <div className="sk-card-meta">
        <span>
          {skill.version != null
            ? versionLabel(skill.version)
            : "Version not reported"}
        </span>
        <span>
          {skill.changedAt
            ? `Updated ${formatExactDate(skill.changedAt)}`
            : "No update date"}
        </span>
        {lastUsed ? (
          <span data-testid="skill-card-last-used">{lastUsed}</span>
        ) : null}
      </div>
      <div className="sk-card-footer">
        <span>
          <BookOpen size={13} />
          Skill details
        </span>
        <strong>
          Explore skill <ArrowRight size={15} />
        </strong>
      </div>
    </button>
  );
}
function HermesReference({ reference }) {
  if (!reference) return null;
  const overview = hermesReferenceText(reference.overview);
  return (
    <section
      className="sk-catalog-reference"
      aria-label="Official Hermes reference"
    >
      <div className="sk-section-label">
        <h3>
          <BookOpen size={16} /> About this capability
        </h3>
        <span>{reference.category}</span>
      </div>
      {overview && <p className="sk-catalog-overview">{overview}</p>}
      {reference.tags.length > 0 && (
        <ul className="sk-catalog-tags" aria-label="Skill topics">
          {reference.tags.slice(0, 6).map((tag) => (
            <li key={tag}>{tag}</li>
          ))}
        </ul>
      )}
      <div className="sk-catalog-footer">
        <a
          href={reference.documentationUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Read Hermes documentation <ExternalLink size={13} />
        </a>
        <p>
          {reference.reference === "archive"
            ? "Official reference for this older skill name."
            : "Official catalog reference. Your installed version may differ."}
        </p>
      </div>
    </section>
  );
}
function HistoryList({ entries }) {
  if (!entries.length)
    return (
      <div className="sk-empty-detail">
        <History size={26} />
        <h3>No changes recorded yet</h3>
        <p>Updates will appear here when your agent reports them.</p>
      </div>
    );
  return (
    <ol className="sk-history">
      {entries.map((entry, index) => (
        <li key={`${entry.id || "change"}-${index}`}>
          <span className="sk-history-dot">
            <History size={13} />
          </span>
          <div>
            <div className="sk-history-heading">
              <strong>{formatActionLabel(entry.action)}</strong>
              <time>{formatExactDate(entry.occurredAt)}</time>
            </div>
            <p>
              {customerSkillText(entry.whatChanged) ||
                "No summary was included for this change."}
            </p>
            {entry.whyChanged && (
              <p className="sk-why">
                <b>Why it changed</b>
                {customerSkillText(entry.whyChanged)}
              </p>
            )}
            {entry.rollbackAvailable && <small>Rollback recorded</small>}
          </div>
        </li>
      ))}
    </ol>
  );
}
function SkillPopup({
  skill,
  all,
  changes,
  workspace,
  onSelect,
  onClose,
  opener,
}) {
  const [tab, setTab] = useState("overview");
  const [goal, setGoal] = useState("");
  const [copied, setCopied] = useState("");
  const closeRef = useRef(null);
  const chat = useChat();
  const visual = skillVisual(skill);
  const Icon = visual.icon;
  const history = skillChanges(skill, changes);
  const index = all.findIndex((item) => skillKey(item) === skillKey(skill));
  const brand = skillGroup(skill) === "brand";
  const hermes = skillGroup(skill) === "hermes";
  const reference = hermesSkillDetails(skill);
  const sharing = formatPromotionLabel(skill.promotionStatus);
  const customization = formatDriftLabel(skill.driftStatus);
  const draft = `I'd like to use the ${skillTitle(skill)} skill.\n\n${goal.trim() ? `What I want to accomplish:\n${goal.trim()}\n\n` : "Ask me what I want to accomplish before starting.\n\n"}Check the skill's instructions and the context you have, and ask for anything missing. Bring the result back as a draft for review. Do not publish, send, or change live settings without my approval.`;
  const allowed =
    ["owner", "admin", "member"].includes(workspace?.role) && !isRemoved(skill);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      setCopied("Draft copied");
    } catch {
      setCopied("Copy wasn’t available. Select the preview text to copy it.");
    }
  };
  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="sk-overlay" />
        <Dialog.Content
          className="sk-popup"
          style={groupStyle(visual)}
          data-testid="skill-detail-popup"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            closeRef.current?.focus();
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (opener.current?.isConnected) opener.current.focus();
          }}
        >
          <div className="sk-popup-scroll">
            <header className="sk-hero">
              <div className="sk-hero-top">
                <span>
                  <Sparkles size={14} />
                  Skills <ChevronRight size={12} />
                  <span className="sk-hero-origin">{skillOrigin(skill)}</span>
                </span>
                <div>
                  <small>
                    {index + 1} / {all.length}
                  </small>
                  <button
                    type="button"
                    aria-label="Previous skill"
                    disabled={all.length < 2}
                    onClick={() =>
                      onSelect(
                        skillKey(all[(index - 1 + all.length) % all.length]),
                      )
                    }
                  >
                    <ArrowLeft size={16} />
                  </button>
                  <button
                    type="button"
                    aria-label="Next skill"
                    disabled={all.length < 2}
                    onClick={() =>
                      onSelect(skillKey(all[(index + 1) % all.length]))
                    }
                  >
                    <ArrowRight size={16} />
                  </button>
                  <Dialog.Close ref={closeRef} aria-label="Close skill details">
                    <X size={18} />
                  </Dialog.Close>
                </div>
              </div>
              <div className="sk-identity">
                <span className="sk-hero-icon">
                  <Icon size={32} />
                </span>
                <div>
                  <span className="sk-hero-eyebrow">
                    {brand
                      ? "TAILORED TO YOUR BRAND"
                      : "PART OF YOUR AGENT’S TOOLKIT"}
                  </span>
                  <Dialog.Title>{skillTitle(skill)}</Dialog.Title>
                  <Dialog.Description>
                    What this skill does, where it comes from, and how it’s
                    evolving.
                  </Dialog.Description>
                </div>
              </div>
            </header>
            <div className="sk-summary">
              <div>
                <Package size={18} />
                <span>
                  <small>Skill origin</small>
                  <strong className="sk-summary-origin">
                    {skillOrigin(skill)}
                  </strong>
                </span>
              </div>
              <div>
                <Layers3 size={18} />
                <span>
                  <small>Current version</small>
                  <strong>{versionLabel(skill.version)}</strong>
                </span>
              </div>
              <div>
                <Clock3 size={18} />
                <span>
                  <small>Last updated</small>
                  <strong>{formatExactDate(skill.changedAt)}</strong>
                </span>
              </div>
            </div>
            <div className="sk-detail-body">
              <Tabs.Root
                value={tab}
                onValueChange={setTab}
                className="sk-detail-main"
              >
                <Tabs.List
                  className="sk-detail-tabs"
                  aria-label="Skill details"
                >
                  {[
                    ["overview", LayoutGrid, "Overview"],
                    ["updates", History, "Updates"],
                    ["use", MessageSquare, "Use this skill"],
                  ].map(([value, TabIcon, label]) => (
                    <Tabs.Trigger key={value} value={value}>
                      <TabIcon size={14} />
                      {label}
                      {value === "updates" && history.length > 0 && (
                        <span>{history.length}</span>
                      )}
                    </Tabs.Trigger>
                  ))}
                </Tabs.List>
                <Tabs.Content value="overview" className="sk-detail-panel">
                  <section>
                    <div className="sk-section-label">
                      <h3>What it does</h3>
                      <span>
                        {usesHermesDescription(skill)
                          ? "Official Hermes reference"
                          : "Skill overview"}
                      </span>
                    </div>
                    <p className="sk-description">
                      {customerSkillDescription(skill) ||
                        "Your agent hasn’t published a description for this skill yet. You can ask about its capabilities in Chat."}
                    </p>
                  </section>
                  <HermesReference reference={reference} />
                  <section className="sk-origin-panel">
                    <span>
                      {brand ? (
                        <Fingerprint size={24} />
                      ) : hermes ? (
                        <Package size={24} />
                      ) : (
                        <Layers3 size={24} />
                      )}
                    </span>
                    <div>
                      <small>
                        {brand
                          ? "YOUR BRAND, BUILT IN"
                          : hermes
                            ? "THE DEFAULT HERMES TOOLKIT"
                            : "YOUR GROWTHOS TOOLKIT"}
                      </small>
                      <h3>
                        {brand
                          ? `Part of ${workspace?.clientName || "your brand"}’s toolkit`
                          : hermes
                            ? "A core agent capability"
                            : "Built to work across GrowthOS"}
                      </h3>
                      <p>
                        {brand
                          ? "This skill is classified as brand-specific in your workspace. It can contain workflows created or customized for your brand."
                          : hermes
                            ? "This capability comes from Hermes, the core agent toolkit. Its original source is preserved here."
                            : "A shared GrowthOS skill recorded in this workspace. Its current status is shown in the skill details."}
                      </p>
                    </div>
                  </section>
                  <section>
                    <div className="sk-section-label">
                      <h3>Latest improvement</h3>
                      {history.length > 0 && (
                        <button onClick={() => setTab("updates")}>
                          All updates <ArrowRight size={13} />
                        </button>
                      )}
                    </div>
                    {history.length ? (
                      <HistoryList entries={history.slice(0, 1)} />
                    ) : (
                      <p className="sk-muted">
                        No change summary has been reported yet.
                      </p>
                    )}
                  </section>
                </Tabs.Content>
                <Tabs.Content value="updates" className="sk-detail-panel">
                  <div className="sk-section-label">
                    <h3>How this skill has evolved</h3>
                  </div>
                  <p className="sk-muted sk-history-intro">
                    Reported changes, newest first. Older changes may not be
                    included.
                  </p>
                  <HistoryList entries={history} />
                </Tabs.Content>
                <Tabs.Content value="use" className="sk-detail-panel">
                  <h3>Put this skill to work</h3>
                  <p className="sk-muted">
                    Give your agent a goal and any useful context. You’ll review
                    the draft in Chat before sending.
                  </p>
                  <label className="sk-goal-label" htmlFor="skill-goal">
                    What would you like to accomplish?
                  </label>
                  <textarea
                    id="skill-goal"
                    maxLength={6500}
                    rows={4}
                    value={goal}
                    onChange={(event) => {
                      setGoal(event.target.value);
                      setCopied("");
                    }}
                    placeholder="Describe the outcome, audience, and anything your agent should know…"
                  />
                  <details className="sk-draft-preview">
                    <summary>Preview your message</summary>
                    <p>{draft}</p>
                  </details>
                  <div className="sk-use-actions">
                    <button
                      type="button"
                      className="sk-secondary"
                      onClick={copy}
                    >
                      <Check size={14} />
                      Copy draft
                    </button>
                    {chat ? (
                      <ChatDraftLaunch
                        chat={chat}
                        draft={draft}
                        allowed={allowed}
                      />
                    ) : (
                      <p>Chat is unavailable here. You can copy your draft.</p>
                    )}
                  </div>
                  {copied && (
                    <p role="status" className="sk-muted">
                      {copied}
                    </p>
                  )}
                </Tabs.Content>
              </Tabs.Root>
              <aside className="sk-detail-aside">
                <h3>
                  Skill details <Fingerprint size={15} />
                </h3>
                <dl>
                  <div>
                    <dt>Workspace</dt>
                    <dd>{workspace?.clientName || "Your workspace"}</dd>
                  </div>
                  <div>
                    <dt>Status</dt>
                    <dd>
                      <SkillState skill={skill} />
                    </dd>
                  </div>
                  <div>
                    <dt>Source</dt>
                    <dd>{skillOrigin(skill)}</dd>
                  </div>
                  {parseTimestamp(skill.lastUsedAt) ? (
                    <div>
                      <dt>Last used</dt>
                      <dd data-testid="skill-detail-last-used">
                        {formatExactDate(skill.lastUsedAt)}
                      </dd>
                    </div>
                  ) : null}
                  {sharing && (
                    <div>
                      <dt>Sharing</dt>
                      <dd>{sharing}</dd>
                    </div>
                  )}
                  {customization && (
                    <div>
                      <dt>Customization</dt>
                      <dd>{customization}</dd>
                    </div>
                  )}
                </dl>
                <div className="sk-aside-note">
                  <BookOpen size={20} />
                  <strong>Skills are how your agent works</strong>
                  <p>
                    Ask for an outcome in Chat. Your agent uses the skill’s
                    instructions to guide the work.
                  </p>
                </div>
              </aside>
            </div>
          </div>
          <footer className="sk-popup-footer">
            <span>
              <BookOpen size={13} />
              Details from your workspace’s skill registry
            </span>
            <div>
              <Dialog.Close className="sk-secondary">
                Done <kbd>esc</kbd>
              </Dialog.Close>
              {tab !== "use" && (
                <button
                  type="button"
                  className="sk-primary"
                  onClick={() => setTab("use")}
                >
                  <MessageSquare size={15} />
                  Use this skill <ArrowRight size={14} />
                </button>
              )}
            </div>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default function SkillLibrary({ skills = [], changes = [], workspace }) {
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState("all");
  const [sort, setSort] = useState("name");
  const [showRemoved, setShowRemoved] = useState(false);
  const [selected, setSelected] = useState(null);
  const opener = useRef(null);
  const visible = visibleSkills(skills, { search, group, sort, showRemoved });
  const clientSkills = skills.filter(isClientVisibleSkill);
  const installed = clientSkills.filter((skill) => !isRemoved(skill));
  const counted = showRemoved ? clientSkills : installed;
  const active = visible.find((skill) => skillKey(skill) === selected);
  const filtered =
    search.trim() || group !== "all" || showRemoved || sort !== "name";
  const reset = () => {
    setSearch("");
    setGroup("all");
    setSort("name");
    setShowRemoved(false);
  };
  return (
    <div className="sk-library" data-testid="skill-library">
      <div className="sk-library-intro">
        <div>
          <span className="sk-eyebrow">
            <Sparkles size={13} />
            THE KNOW-HOW BEHIND THE WORK
          </span>
          <h2>Your agent’s skill library</h2>
          <p>
            Explore what your agent can do. Open a skill to see the details,
            latest improvements, and ways to put it to work.
          </p>
        </div>
        <div className="sk-library-count">
          <strong>{installed.length}</strong>
          <span>skills in your toolkit</span>
        </div>
      </div>
      <div className="sk-browse">
        <div className="sk-browse-heading">
          <h3>Browse by skill group</h3>
          <p>Choose a group, then open a card to explore what it can do.</p>
        </div>
        <div className="sk-group-filter" role="group" aria-label="Skill group">
          {FILTER_GROUPS.map((item) => {
            const count = (
              item.key === "all"
                ? counted
                : counted.filter((skill) => skillGroup(skill) === item.key)
            ).length;
            return (
              <button
                type="button"
                key={item.key}
                style={groupStyle(item)}
                aria-label={`${item.title} ${count}`}
                aria-pressed={group === item.key}
                onClick={() => setGroup(item.key)}
              >
                <span className="sk-filter-icon" aria-hidden="true">
                  <item.icon size={22} />
                </span>
                <span className="sk-filter-count">{count}</span>
                <span className="sk-filter-title">{item.title}</span>
                <span className="sk-filter-hint">{item.hint}</span>
                <span className="sk-filter-selected" aria-hidden="true">
                  <Check size={12} strokeWidth={3} />
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <div className="sk-toolbar">
        <label className="sk-search">
          <Search size={17} />
          <input
            aria-label="Search skills"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Find a skill by name or description…"
          />
        </label>
        <select
          aria-label="Sort skills"
          value={sort}
          onChange={(event) => setSort(event.target.value)}
        >
          <option value="name">Name: A–Z</option>
          <option value="updated">Recently updated</option>
        </select>
      </div>
      <div className="sk-result-line">
        <span role="status">
          {visible.length} {visible.length === 1 ? "skill" : "skills"}
          {search.trim() ? ` matching “${search.trim()}”` : " in view"}
        </span>
        <div>
          <label>
            <input
              type="checkbox"
              checked={showRemoved}
              onChange={(event) => setShowRemoved(event.target.checked)}
            />
            Include removed
          </label>
          {filtered && (
            <button type="button" onClick={reset}>
              <X size={12} />
              Reset filters
            </button>
          )}
        </div>
      </div>
      {GROUPS.filter((section) => group === "all" || group === section.key).map(
        (section) => {
          const items = visible.filter(
            (skill) => skillGroup(skill) === section.key,
          );
          if (!items.length && (search.trim() || group !== "all")) return null;
          return (
            <section
              className={`sk-group sk-group-${section.key}`}
              style={groupStyle(section)}
              key={section.key}
              aria-label={section.title}
            >
              <header>
                <span className="sk-group-icon">
                  <section.icon size={21} />
                </span>
                <div>
                  <span className="sk-eyebrow">{section.eyebrow}</span>
                  <h2>
                    {section.title} <span>{items.length}</span>
                  </h2>
                  <p>{section.description}</p>
                </div>
              </header>
              {items.length ? (
                <div className="sk-grid">
                  {items.map((skill) => (
                    <SkillCard
                      key={skillKey(skill)}
                      skill={skill}
                      onOpen={(event) => {
                        opener.current = event.currentTarget;
                        setSelected(skillKey(skill));
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div className="sk-empty-group">
                  No {section.title} skills have been published in this
                  workspace yet.
                </div>
              )}
            </section>
          );
        },
      )}
      {visible.length === 0 && (search.trim() || group !== "all") && (
        <div className="sk-empty-detail">
          <Search size={28} />
          <h3>No matching skills</h3>
          <p>Try a different search or show all skill groups.</p>
          <button type="button" className="sk-secondary" onClick={reset}>
            Clear filters
          </button>
        </div>
      )}
      {active && (
        <SkillPopup
          key={skillKey(active)}
          skill={active}
          all={visible}
          changes={changes}
          workspace={workspace}
          onSelect={setSelected}
          onClose={() => setSelected(null)}
          opener={opener}
        />
      )}
    </div>
  );
}
